/**
 * Everything both views need, wired together once.
 *
 * The project now has two front ends: the lab at `/`, which is eight diagnostic
 * panels and sixty controls, and the piece at `/perform`, which is a line of
 * type. They are completely different pages and they run exactly the same
 * machine — three ring buffers, a face landmarker, an audio graph, a recogniser,
 * a gesture classifier, a timeline and a recorder, with two watchdogs and a wake
 * lock holding it together.
 *
 * That machine lived inside the lab's `main.js` until there was a second page to
 * want it. Keeping it there and importing across would have made the piece
 * depend on the debug page booting, which is exactly backwards: the piece is the
 * deliverable and the lab is the instrument for judging it.
 *
 * ### No DOM beyond the video element
 *
 * The one unavoidable piece of page is `<video>`, because `getUserMedia` has to
 * put its stream somewhere and MediaPipe reads frames off it. Everything else —
 * meters, overlays, captions, panels — belongs to whichever view asked for it.
 * The engine reports through callbacks and holds no opinion about what is drawn.
 *
 * ### The frame loop lives here
 *
 * There is exactly one `requestAnimationFrame` loop in a running page, and it is
 * this one. Both watchdogs are driven from it, because it is the only thing
 * still running when the camera's own callback has stopped firing. Views get
 * `onFrame` and do their drawing inside it rather than starting a second loop
 * that would compete for the same frames.
 */

import { AudioCapture, audioSupported } from './audio/capture.js';
import { GestureDetector } from './audio/gestures.js';
import { config } from './core/config.js';
import { Recorder } from './core/recorder.js';
import { RingBuffer } from './core/ringbuffer.js';
import { Timeline } from './core/timeline.js';
import { tuning } from './core/tunables.js';
import { languageAvailability, Recognition, recognitionSupported } from './speech/recognition.js';
import { Transcript } from './speech/transcript.js';
import { Landmarker, visionSupported } from './vision/landmarker.js';

export { audioSupported, recognitionSupported, visionSupported };

/**
 * Milliseconds without a face before the recording is told about it.
 *
 * Not the camera watchdog — that one asks whether frames are arriving, and it is
 * right to stay quiet here, because they are. This asks the different question
 * of whether anybody is in front of the lens.
 *
 * A take made while reading this page lost its face for forty of sixty-nine
 * seconds: frames kept coming, `landmarker.state` stayed 'running', and the only
 * trace was a hole in the mouth column that nothing explained. Thirty-eight
 * gestures were classified in that hole, every one of them at confidence 0.60,
 * because the vision gate correctly refused to judge what it could not see.
 *
 * A recording that cannot distinguish "the camera stopped" from "the speaker
 * looked away" makes both of them look like a bug in the detector.
 */
const FACE_LOST_MS = 1500;

/**
 * How long the recogniser may be spoken to and stay silent before saying so.
 *
 * Not a tunable: it is a diagnostic threshold rather than a rendering or
 * detection one, and there is no take where the right answer is different. Long
 * enough that a slow first result on a cold connection is not an alarm, short
 * enough to catch the failure inside one attempt at a sentence.
 */
const ASR_MUTE_MS = 12_000;

export class Engine {
  /**
   * @param {object} o
   * @param {HTMLVideoElement} o.video
   * @param {(channel: string, state: string, detail?: string) => void} [o.onState]
   * @param {(ctx: {now: number, audioFrame: ?object}) => void} [o.onFrame]
   * @param {(event: object) => void} [o.onRelease] due off the timeline, in
   *   spoken order. Where a view places a gesture on its line.
   * @param {(diff: object, result: object) => void} [o.onResult] recogniser said
   *   something, transcript already updated.
   * @param {(sample: object) => void} [o.onSample] one MouthSample, at camera rate
   * @param {(event: object) => void} [o.onGesture] classified and gated, before
   *   the timeline holds it back
   */
  constructor({ video, onState, onFrame, onRelease, onResult, onSample, onGesture } = {}) {
    this.video = video;
    this.onState = onState ?? (() => {});
    this.onFrame = onFrame ?? (() => {});
    this.onRelease = onRelease ?? (() => {});
    this.onResult = onResult ?? (() => {});
    this.onGesture = onGesture ?? (() => {});

    /** Mouth state, at camera rate. */
    this.mouthBuffer = new RingBuffer(config.buffers.mouth);
    /** rAF-rate feature frames. Phase 6 classifies from these; GRAD reads them. */
    this.audioBuffer = new RingBuffer(config.buffers.audio);
    /** ~375 Hz level envelope. Phase 5 FFTs this to find trills. */
    this.envelopeBuffer = new RingBuffer(config.buffers.envelope);

    this.recorder = new Recorder();
    this.timeline = new Timeline();
    this.transcript = new Transcript({
      mouthBuffer: this.mouthBuffer,
      audioBuffer: this.audioBuffer,
    });

    this.landmarker = new Landmarker({
      video,
      buffer: this.mouthBuffer,
      // Straight off the frame callback rather than polled from the ring buffer,
      // so a recording keeps every sample even when the render loop stutters or
      // the buffer wraps.
      onSample: (sample) => {
        this._lastFaceAt = sample.t;
        if (this.faceLost) {
          this.faceLost = false;
          this.recorder.event('face:found');
          this.onState('face', 'found');
        }
        this.recorder.push(sample);
        onSample?.(sample);
      },
      onState: (state, detail) => this._report('camera', state, detail),
    });

    this.gestures = new GestureDetector({
      mouthBuffer: this.mouthBuffer,
      onGesture: (event) => this._onGesture(event),
    });

    this.capture = new AudioCapture({
      buffer: this.envelopeBuffer,
      onEnvelope: (sample) => this.recorder.pushEnvelope(sample),
      // Trills arrive already formed from the envelope FFT and go through the
      // classifier only to be gated by vision and given a place.
      onGesture: (event) => this.gestures.trill(event),
      onState: (state, detail) => this._report('mic', state, detail),
    });

    this.recognition = new Recognition({
      onState: (state, detail) => this._report('asr', state, detail),
      onResult: (result) => {
        this._lastResultAt = performance.now();
        this._asrMute = false;
        const diff = this.transcript.update(result);
        this.recorder.speech({ ...result, at: result.now, diff });
        this.onResult(diff, result);
      },
    });

    /** performance.now() of the last transcript result, and of the last loud frame. */
    this._lastResultAt = 0;
    this._loudSince = 0;
    /** Reported once per silent spell rather than every frame. */
    this._asrMute = false;

    /** How Channel A's language was decided, for the recording to carry. */
    this.languageSource = 'default';

    /** performance.now() of the last frame a face was found in. */
    this._lastFaceAt = 0;
    /** Frames are arriving but nobody is in front of the camera. */
    this.faceLost = false;

    this._wakeLock = null;
    this._raf = 0;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.recorder.recording && !this._wakeLock) {
        this._holdScreenAwake();
      }
    });
  }

  _report(channel, state, detail) {
    // Recorded, so a take can explain its own gaps. A stall is exactly the thing
    // that would otherwise look like a stretch of very steady audio.
    this.recorder.event(`${channel}:${state}${detail ? ` (${detail})` : ''}`);
    this.onState(channel, state, detail);
  }

  _onGesture(event) {
    const detail = event.modRate ? `${event.modRate.toFixed(1)} Hz` : `${event.dur | 0} ms`;
    this.timeline.push({ ...event, label: `${event.type} ${detail}` });
    this.recorder.event(
      `gesture:${event.type} ${detail} conf=${event.confidence.toFixed(2)}` +
        // Whether anybody was in shot when this was classified. Without it, a
        // confidence of 0.60 is ambiguous between "no camera" and "camera on,
        // speaker looking elsewhere" — and only one of those is worth trusting.
        (this.faceLost ? ' noface' : ''),
    );
    this.onGesture(event);
  }

  /** Is either channel open? Enough to record. */
  get capturing() {
    return this.landmarker.active || this.capture.active;
  }

  /**
   * Keep the screen awake while recording.
   *
   * Best-effort by design. Screen Wake Lock is unevenly supported and can be
   * refused; a take without it is a take that might lose its camera, which is
   * worth a line in the status rather than a refusal to record.
   */
  async _holdScreenAwake() {
    try {
      this._wakeLock = (await navigator.wakeLock?.request('screen')) ?? null;
      this._wakeLock?.addEventListener?.('release', () => {
        this._wakeLock = null;
      });
    } catch {
      this._wakeLock = null;
    }
  }

  _releaseScreen() {
    this._wakeLock?.release?.().catch(() => {});
    this._wakeLock = null;
  }

  /** The applied capture settings, not the requested ones. */
  audioEnv() {
    const s = this.capture.stats;
    return {
      sampleRate: s.sampleRate,
      blockSize: s.blockSize,
      envelopeRate: s.envelopeRate,
      settings: s.settings,
      ignored: s.ignored,
      unreported: s.unreported,
    };
  }

  startRecording(note = '') {
    const v = this.video;
    this.recorder.start(
      {
        userAgent: navigator.userAgent,
        // What the browser says it is set to, next to what Channel A was actually
        // asked for. The two differ whenever somebody performs in a language
        // their phone is not configured in, which is the ordinary case and reads
        // from the outside as "it is not catching my words" — and until this was
        // recorded the only way to tell was to infer it from the tuning diff.
        language: navigator.language,
        recognitionLang: config.speech.lang,
        // Which of url / saved / browser / default decided it. Without this the
        // only way to ask why a take opened in the wrong language was to infer
        // it from a tuning diff, and a diff cannot represent a language that
        // equals the shipped default — so the question had no answer at all.
        languageSource: this.languageSource,
        video: { width: v.videoWidth, height: v.videoHeight, aspect: v.videoWidth / v.videoHeight },
        delegate: this.landmarker.stats.delegate,
        fps: this.landmarker.stats.fps,
        // Whether noise suppression was actually off is the first question to
        // ask of any recording where Channel B looks broken.
        audio: this.audioEnv(),
      },
      note,
    );
    this._holdScreenAwake();
    return this.recorder;
  }

  stopRecording() {
    this._releaseScreen();
    return this.recorder.stop();
  }

  /**
   * One take: every capture on, recording started, all of it stopped together.
   *
   * Worth having as one call rather than four clicks. The clicks take several
   * seconds, which is several seconds of recording where the channels do not
   * agree about what exists — and a take whose marks were made while still
   * reaching for the next button is a take whose labels describe the wrong
   * moments.
   *
   * The camera is started; the video is not stored. Only landmarks are.
   *
   * @returns {Promise<string[]>} what failed to come up, if anything
   */
  async startAll({ note = '', record = true } = {}) {
    const failures = [];

    if (visionSupported() && this.landmarker.state !== 'running') {
      await this.landmarker.start().catch((e) => failures.push(`camera: ${e?.message ?? e}`));
    }
    if (audioSupported() && !this.capture.active) {
      await this.capture.start().catch((e) => failures.push(`microphone: ${e?.message ?? e}`));
    }
    if (recognitionSupported() && !this.recognition.listening) this.recognition.start();

    // Record whatever came up. A take with two of three channels is still a
    // take, and refusing to record it would throw away the one that was working.
    if (record && !this.recorder.recording) this.startRecording(note);

    // Channel A's opening state, written after the recorder exists. It used to
    // be reported during `recognition.start()` above — before there was anything
    // to write to — so a take where the recogniser never said another word held
    // no evidence it had ever started at all, and "no transcript" and "never
    // listening" were indistinguishable in the log.
    this.recorder.event(
      recognitionSupported()
        ? `asr:${this.recognition.state} (${config.speech.lang})`
        : 'asr:unsupported',
    );

    this._lastResultAt = performance.now();
    this._loudSince = 0;
    this._asrMute = false;

    // Asked, not awaited: a take must not wait on it, and a browser with no
    // opinion is the common case. When there is an answer it is the difference
    // between "nothing is happening" and "this browser has no model for the
    // language you picked", which is the whole question on a silent channel.
    if (recognitionSupported()) {
      languageAvailability(config.speech.lang).then((status) => {
        if (!status || status === 'available') return;
        this.recorder.event(`asr:model ${status} (${config.speech.lang})`);
        this.onState('asr', `model-${status}`, `${config.speech.lang}: model ${status}`);
      });
    }
    this._lastFaceAt = performance.now();
    this.faceLost = false;
    return failures;
  }

  /**
   * The recording is stopped FIRST, so the session ends cleanly rather than
   * trailing however long it took to release three devices.
   */
  stopAll() {
    const session = this.recorder.recording ? this.stopRecording() : null;
    if (this.recognition.listening) this.recognition.stop();
    // Anything mid-gesture is closed before the graph goes away, so a take that
    // ends during a sustained sound still reports it.
    this.gestures.flush();
    this.capture.stop();
    this.landmarker.stop();
    return session;
  }

  /**
   * Is a face being found, and say so once when that changes.
   *
   * Deliberately separate from `Landmarker.check`, which asks whether frames are
   * arriving at all. Both can be true at once and they mean different things —
   * see FACE_LOST_MS.
   */
  _checkFace(now) {
    if (this.landmarker.state !== 'running') return;
    const quiet = now - this._lastFaceAt;
    if (!this.faceLost && quiet > FACE_LOST_MS) {
      this.faceLost = true;
      this.recorder.event('face:lost');
      this.onState('face', 'lost', `${Math.round(quiet)} ms`);
    }
  }

  /** Start the one render loop. */
  start() {
    if (this._raf) return;
    const tick = () => {
      this._frame();
      this._raf = requestAnimationFrame(tick);
    };
    this._raf = requestAnimationFrame(tick);
  }

  stop() {
    cancelAnimationFrame(this._raf);
    this._raf = 0;
  }

  /**
   * A recogniser that is listening, is being spoken to, and says nothing.
   *
   * The failure this catches produced no results, no errors and no end events
   * for 340 seconds across two takes while the microphone was reading -13 dB —
   * and the only symptom available to the person in front of it was an empty
   * line. Channel B kept working the whole time, so "the audio is fine" and "the
   * transcript is dead" were true at once and nothing on the page said either.
   *
   * Loudness comes from the same `db` Channel B gates on, so this cannot fire in
   * a quiet room, and the timer only runs while there is sound to transcribe: a
   * long thoughtful pause is not evidence of anything.
   */
  _checkTranscript(now, audioFrame) {
    if (!this.recognition.listening || this.recognition.state !== 'listening') {
      this._loudSince = 0;
      return;
    }
    const loud = audioFrame && audioFrame.db > config.audio.SILENCE_DB;
    if (!loud) return;
    if (!this._loudSince) this._loudSince = now;

    const spokenFor = now - this._loudSince;
    const sinceResult = now - this._lastResultAt;
    if (this._asrMute || spokenFor < ASR_MUTE_MS || sinceResult < ASR_MUTE_MS) return;

    this._asrMute = true;
    const detail = `nothing from ${config.speech.lang} in ${Math.round(spokenFor / 1000)}s`;
    this.recorder.event(`asr:silent (${detail})`);
    this.onState('asr', 'silent', detail);
  }

  _frame() {
    const now = performance.now();

    // The camera's watchdog. Driven from here because the render loop is the
    // only thing still running once the frame callback has stopped firing — and
    // it stops without saying so.
    this.landmarker.check(now);
    this._checkFace(now);

    let audioFrame = null;
    if (this.capture.active) {
      // Called unconditionally, because this is where the stall watchdog lives.
      // A null return is the graph saying it has nothing true to report, and
      // nothing downstream may invent a value to fill the gap — that is the
      // whole lesson of the take where twenty-three seconds of a frozen buffer
      // were recorded as measurements.
      audioFrame = this.capture.frame(now);
      if (audioFrame) {
        this.audioBuffer.push(audioFrame);
        this.recorder.pushAudio(audioFrame);
        // Full rate. A plosive is over inside one panel tick, so throttling this
        // would be throttling the detector.
        this.gestures.push(audioFrame);
      }
      // The block size, and therefore the envelope rate, is only known once the
      // first worklet batch has landed — which is after a take has already
      // started. Kept current rather than snapshotted before it existed.
      if (this.recorder.recording && this.capture.stats.blockSize) {
        this.recorder.updateEnv({ audio: this.audioEnv() });
      }
    }

    // After the audio, because it needs this frame's loudness to know whether
    // silence from the recogniser means anything.
    this._checkTranscript(now, audioFrame);

    // The clock merge, and the whole reason Timeline exists. A word reported now
    // was spoken ~300 ms ago and a plosive ~15 ms ago; both were pushed with
    // their own detectLatency, and both come back out here in the order they
    // actually happened.
    for (const event of this.timeline.due(now)) this.onRelease(event);

    this.onFrame({ now, audioFrame });
  }

  /** What was moved away from defaults, for a view that wants to say so. */
  get tuning() {
    return tuning();
  }
}

/**
 * Upload a finished session.
 *
 * Every recording goes to R2. Uploading is not a decision worth making one take
 * at a time, and a recording that only exists in a tab is a recording that is
 * about to be lost.
 */
export async function uploadSession(session) {
  const res = await fetch('/api/sessions', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(session),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export default Engine;
